import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { SignaturePad } from "../components/SignaturePad";
import { GuidelineImportDialog } from "../components/GuidelineImportDialog";
import TaskVideosCard from "../components/TaskVideosCard";

export const TASK_CODES = [
  ["106", "Hair Care – Shampoo"],
  ["115", "Meal Preparation"], ["116", "Housework"], ["117", "Managing Finances"], ["118", "Managing Medications"],
  ["119", "Shopping"], ["120", "Transportation"], ["122", "Hygiene"], ["123", "Dressing – Upper Body"],
  ["124", "Dressing – Lower Body"], ["125", "Locomotion"], ["126", "Transfers"], ["127", "Toilet Use"],
  ["128", "Bed Mobility"], ["129", "Eating"], ["130", "Bladder Incontinence"], ["131", "Bowel Incontinence"],
  ["132", "Other Personal Care"], ["134", "Bathing – Wash-Up"], ["135", "Bathing – Shower"], ["137", "Lotion"],
  ["138", "Laundry"], ["139", "Reading/Writing"], ["140", "Supervision/Cueing"], ["141", "Incontinence Care"], ["145", "Stairs"],
];

export const codeLabel = (c) => {
  const hit = TASK_CODES.find(([n]) => n === String(c));
  return hit ? `${hit[0]} ${hit[1]}` : String(c);
};

export default function GuidelinesPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" && sessionStorage.getItem("shc_view_staff") !== "1";
  const [guidelines, setGuidelines] = useState(null);
  const [reviewing, setReviewing] = useState(null);
  const [ack, setAck] = useState(false);
  const [sig, setSig] = useState(null);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null);
  const [rosterFor, setRosterFor] = useState(null);

  const load = () => api.get("/guidelines").then((r) => setGuidelines(r.data)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, []);

  const signOff = async () => {
    if (!ack) { toast.error("Please confirm you have read the guidelines"); return; }
    if (!sig) { toast.error("Please draw your signature"); return; }
    setBusy(true);
    try {
      await api.post(`/guidelines/${reviewing.id}/signoff`, { signature_png: sig });
      toast.success("Signed off — this covers you for every client");
      setReviewing(null);
      setAck(false);
      setSig(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const saveGuideline = async () => {
    if (!editing.title.trim() || !editing.content.trim()) { toast.error("Title and content are required"); return; }
    setBusy(true);
    try {
      const payload = { title: editing.title.trim(), content: editing.content.trim(), task_codes: editing.task_codes };
      if (editing.id) {
        const { data } = await api.put(`/guidelines/${editing.id}`, payload);
        toast.success(data.version > editing.version ? `Saved as version ${data.version} — staff will re-acknowledge` : "Saved");
      } else {
        await api.post("/guidelines", payload);
        toast.success("Guideline created");
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  if (!guidelines) {
    return <div className="flex justify-center py-24"><div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" /></div>;
  }

  return (
    <div className="max-w-3xl" data-testid="guidelines-page">
      <div className="mb-8 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-4xl font-semibold tracking-tight text-stone-900">Agency Guidelines &amp; Training</h1>
          <p className="mt-1 text-sm text-stone-500">
            Task-specific company guidelines. Review and sign off once — your sign-off covers every client whose care plan includes that task.
          </p>
        </div>
        {isAdmin && (
          <div className="flex gap-2">
            <GuidelineImportDialog onApplied={load} />
            <Button onClick={() => setEditing({ title: "", content: "", task_codes: [], version: 1, due_days: 7 })}
              className="bg-primary text-primary-foreground hover:bg-stone-900" data-testid="new-guideline-button">
              + New Guideline
            </Button>
          </div>
        )}
      </div>

      {isAdmin && <TaskVideosCard />}

      <div className="space-y-4" data-testid="guidelines-list">
        {guidelines.length === 0 && (
          <p className="rounded-lg border border-dashed border-stone-300 bg-white p-8 text-center text-sm text-stone-500">
            No guidelines yet.{isAdmin ? " Create the first one — e.g. your financial assistance policy for task 117." : ""}
          </p>
        )}
        {[...guidelines].sort((a, b) => ((b.title || "").startsWith("Mission, Values") ? 1 : 0) - ((a.title || "").startsWith("Mission, Values") ? 1 : 0)).map((g) => (
          <div key={g.id} className={`rounded-lg border bg-white p-5 shadow-sm ${(g.title || "").startsWith("Mission, Values") ? "border-primary/40" : "border-stone-200"}`} data-testid={`guideline-${g.id}`}>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <p className="font-heading text-sm font-semibold text-stone-900">
                  {g.title}
                  {(g.title || "").startsWith("Mission, Values") && (
                    <span className="ml-2 rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-semibold text-primary" data-testid="guideline-pinned-badge">Operational philosophy — start here</span>
                  )}
                </p>
                <div className="mt-1 flex flex-wrap gap-1">
                  {(g.task_codes || []).map((c) => (
                    <span key={c} className="rounded-full bg-accent px-2 py-0.5 text-[10px] font-medium text-accent-foreground">{codeLabel(c)}</span>
                  ))}
                </div>
              </div>
              <div className="flex shrink-0 flex-col items-end gap-1.5">
                {g.needs_rereview ? (
                  <span className="rounded-full border border-[#B7791F]/30 bg-[#B7791F]/10 px-3 py-1 text-xs font-medium text-[#8a5d14]" data-testid={`guideline-rereview-${g.id}`}>Updated — re-review</span>
                ) : g.signed_by_me ? (
                  <span className="rounded-full border border-[#27AE60]/30 bg-[#27AE60]/10 px-3 py-1 text-xs font-medium text-[#1e8449]" data-testid={`guideline-signed-${g.id}`}>Signed off {String(g.signed_by_me.signed_at).slice(0, 10)}</span>
                ) : (
                  <span className="rounded-full border border-[#C0392B]/30 bg-[#C0392B]/10 px-3 py-1 text-xs font-medium text-[#C0392B]" data-testid={`guideline-needed-${g.id}`}>Review needed</span>
                )}
                <div className="flex gap-2">
                  <button onClick={() => { setReviewing(g); setAck(false); setSig(null); }}
                    className="text-xs font-medium text-primary hover:underline" data-testid={`guideline-review-${g.id}`}>
                    {g.signed_by_me && !g.needs_rereview ? "View" : "Review & sign off"}
                  </button>
                  {isAdmin && (
                    <>
                      <button onClick={() => setEditing({ id: g.id, title: g.title, content: g.content, task_codes: g.task_codes || [], version: g.version })}
                        className="text-xs font-medium text-stone-500 hover:underline" data-testid={`guideline-edit-${g.id}`}>Edit</button>
                      <button onClick={() => setRosterFor(rosterFor === g.id ? null : g.id)}
                        className="text-xs font-medium text-stone-500 hover:underline" data-testid={`guideline-roster-${g.id}`}>
                        Roster ({g.signoff_count})
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
            {isAdmin && rosterFor === g.id && (
              <div className="mt-3 border-t border-stone-100 pt-3" data-testid={`guideline-roster-list-${g.id}`}>
                {(g.signoffs || []).length === 0 && <p className="text-xs text-stone-400">No sign-offs yet.</p>}
                {(g.signoffs || []).map((s) => (
                  <p key={s.email} className="text-xs text-stone-600">
                    {s.name} — {String(s.signed_at).slice(0, 10)} (v{s.version})
                    {s.version < g.version && <span className="ml-1 font-medium text-[#B7791F]">— needs re-review</span>}
                  </p>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      {reviewing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="guideline-review-modal">
          <div className="max-h-[85vh] w-full max-w-lg overflow-y-auto rounded-lg bg-white p-6 shadow-xl">
            <h2 className="font-heading text-lg font-semibold text-stone-900">{reviewing.title}</h2>
            <p className="mt-0.5 text-xs text-stone-400">Version {reviewing.version}</p>
            {reviewing.task_video_url && (
              <video controls preload="metadata" src={reviewing.task_video_url} className="mt-3 aspect-video w-full rounded-lg bg-stone-900" data-testid="guideline-task-video" />
            )}
            <div className="mt-3 max-h-64 overflow-y-auto rounded-lg border border-stone-200 bg-accent/40 p-4" data-testid="guideline-content">
              <p className="whitespace-pre-wrap text-sm leading-relaxed text-stone-700">{reviewing.content}</p>
            </div>
            {(!reviewing.signed_by_me || reviewing.needs_rereview) ? (
              <div className="mt-4 space-y-4">
                <label className="flex items-start gap-2 text-sm text-stone-700">
                  <input type="checkbox" className="mt-1" checked={ack} onChange={(e) => setAck(e.target.checked)} data-testid="guideline-ack" />
                  <span>I have read and understand these guidelines and agree to follow them with every client whose care plan includes these tasks.</span>
                </label>
                <SignaturePad onChange={setSig} />
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setReviewing(null)} className="border-stone-300 text-stone-500" data-testid="guideline-review-cancel">Cancel</Button>
                  <Button onClick={signOff} disabled={busy} className="bg-primary text-primary-foreground hover:bg-stone-900" data-testid="guideline-signoff-submit">
                    {busy ? "Signing..." : "Sign Off"}
                  </Button>
                </div>
              </div>
            ) : (
              <div className="mt-4 flex justify-end">
                <Button variant="outline" onClick={() => setReviewing(null)} className="border-stone-300 text-stone-500" data-testid="guideline-review-close">Close</Button>
              </div>
            )}
          </div>
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="guideline-edit-modal">
          <div className="max-h-[85vh] w-full max-w-lg overflow-y-auto rounded-lg bg-white p-6 shadow-xl">
            <h2 className="font-heading text-lg font-semibold text-stone-900">{editing.id ? "Edit guideline" : "New guideline"}</h2>
            {editing.id && <p className="mt-1 text-xs text-[#B7791F]">Changing the content bumps the version — staff will be asked to re-acknowledge.</p>}
            <div className="mt-4 space-y-4">
              <div className="space-y-1.5">
                <Label>Title</Label>
                <Input value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} className="border-stone-300 bg-white" data-testid="guideline-title-input" />
              </div>
              <div className="space-y-1.5">
                <Label>Linked task codes (guideline applies when a care plan includes these)</Label>
                <div className="grid grid-cols-2 gap-1" data-testid="guideline-task-codes">
                  {TASK_CODES.map(([code, label]) => (
                    <label key={code} className="flex items-center gap-1.5 text-xs text-stone-600">
                      <input type="checkbox" checked={editing.task_codes.includes(code)}
                        onChange={(e) => setEditing({ ...editing, task_codes: e.target.checked ? [...editing.task_codes, code] : editing.task_codes.filter((c) => c !== code) })}
                        data-testid={`guideline-code-${code}`} />
                      {code} {label}
                    </label>
                  ))}
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>Guideline content</Label>
                <Textarea rows={8} value={editing.content} onChange={(e) => setEditing({ ...editing, content: e.target.value })}
                  placeholder="The agency policy staff must review — what to do, what never to do, when to call the office..."
                  className="border-stone-300 bg-white" data-testid="guideline-content-input" />
                <div className="space-y-1.5">
                  <Label>Sign-off window (days)</Label>
                  <Input type="number" min="1" value={editing.due_days ?? 7} onChange={(e) => setEditing({ ...editing, due_days: e.target.value })}
                    className="w-32 border-stone-300 bg-white" data-testid="guideline-due-days-input" />
                  <p className="text-[11px] text-stone-400">Staff must sign within this many days of the guideline becoming relevant to them (or being updated). Past the window, it blocks client work.</p>
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setEditing(null)} className="border-stone-300 text-stone-500" data-testid="guideline-edit-cancel">Cancel</Button>
                <Button onClick={saveGuideline} disabled={busy} className="bg-primary text-primary-foreground hover:bg-stone-900" data-testid="guideline-save">
                  {busy ? "Saving..." : "Save Guideline"}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
